import React, { useRef, useState } from "react";
import styled from "styled-components";
import dayjs from "dayjs";
import axios from "axios";
import * as XLSX from "xlsx";
import PageTitle from "../../components/DefaultStyles/PageTitle";
import LineBox from "../../components/DefaultStyles/LineBox";
import Flex from "../../components/DefaultStyles/Flex";
import ConfirmBtn from "../../components/Button/ConfirmBtn";
import { Input } from "../../components/Input/Input";
import { Textarea } from "../../components/Input/Textarea";
import {
  ChatSection,
  ChatSectionTitle,
} from "../../components/pages/Party/ChatInfo/ChatSearch/ChatSearchResult";
import Table, { TableBody, TableBodyItem } from "../../components/Table/Table";
import Loader from "../../components/DefaultStyles/Loader";

interface IPushHistory {
  id: number;
  uid: string;
  time: string;
  title: string;
  body: string;
  status: string;
}

export default function AppPush() {
  const [loading, setLoading] = useState(false);

  // 발송하기 폼 상태
  const [pushForm, setPushForm] = useState({
    time: dayjs().format("YYYY-MM-DD HH:mm:ss"),
    title: "",
    body: "",
    list: [] as string[], // 추가된 부분: 발송 대상 사용자 ID 리스트
  });

  // 모두에게 발송 체크박스
  const [sendToAll, setSendToAll] = useState(false);

  // 발송 내역 (API 연동 전 임시 데이터)
  const [pushHistory, setPushHistory] = useState<IPushHistory[]>([]);

  const fileInputRef = useRef<HTMLInputElement>(null);

  const tableHead = ["발송 시간", "제목", "내용", "상태"];

  // 엑셀 파일 업로드 핸들러
  const handleExcelUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (evt) => {
      const data = evt.target?.result;
      if (!data) return;

      const workbook = XLSX.read(data, { type: "array" });
      const sheet = workbook.Sheets[workbook.SheetNames[0]];
      const rows: any[][] = XLSX.utils.sheet_to_json(sheet, { header: 1 });

      if (rows.length < 2) {
        alert("엑셀 파일에 데이터가 없습니다.");
        return;
      }

      // 헤더 행(첫 번째 행)을 건너뛰고 데이터 행만 처리
      const dataRows = rows.slice(1);

      const uids: string[] = [];
      let title = "";
      let body = "";
      let time = "";

      for (const row of dataRows) {
        // UID (1열)
        const uid = row[0] != null ? String(row[0]).trim() : "";
        if (uid.length > 0) uids.push(uid);

        // PSH_TITLE (2열) - 첫 번째 유효한 값 사용
        if (!title && row[1] != null) {
          title = String(row[1]).trim();
        }
        // PSH_DESC (3열) - 첫 번째 유효한 값 사용
        if (!body && row[2] != null) {
          body = String(row[2]).trim();
        }
        // 발송일시 (4열) - 첫 번째 유효한 값 사용
        if (!time && row[3] != null) {
          const raw = String(row[3]).trim();
          // "2026-03-12 16:00" → "2026-03-12 16:00:00" 형식 보정
          time = raw.length === 16 ? raw + ":00" : raw;
        }
      }

      if (uids.length === 0) {
        alert("엑셀 파일에서 uid를 찾을 수 없습니다.");
        return;
      }

      const uniqueUids = [...new Set(uids)];
      setPushForm({
        list: uniqueUids,
        title: title || pushForm.title,
        body: body || pushForm.body,
        time: time || pushForm.time,
      });
      setSendToAll(false);
      alert(
        `${uids.length}개의 uid를 읽었습니다. (중복 제거 후 총 ${uniqueUids.length}개)`,
      );
    };
    reader.readAsArrayBuffer(file);

    // input 초기화 (같은 파일 재선택 가능)
    e.target.value = "";
  };

  // 모두에게 발송 체크박스 핸들러
  const handleSendToAllChange = (checked: boolean) => {
    if (checked) {
      if (pushForm.list.length > 0) {
        const confirmed = window.confirm(
          "기존 리스트를 모두 삭제하고 전체 유저에게 발송하시겠습니까?",
        );
        if (!confirmed) return;
      }
      setPushForm({ ...pushForm, list: [] });
      setSendToAll(true);
    } else {
      setSendToAll(false);
    }
  };

  // 푸시 발송 핸들러
  const handleSendPush = async () => {
    if (pushForm.list.length === 0) {
      alert(
        "현재는 전체에게 발송은 불가능합니다.\n엑셀 파일을 업로드하여 발송 대상을 지정해주세요.",
      );
      return;
    }
    if (!pushForm.title.trim()) {
      alert("제목을 입력해주세요.");
      return;
    }
    if (!pushForm.body.trim()) {
      alert("내용을 입력해주세요.");
      return;
    }

    const confirmSend = window.confirm(
      `앱 푸시를 발송하시겠습니까?\n\n발송 시간: ${pushForm.time}\n제목: ${pushForm.title}\n내용: ${pushForm.body} \n대상: ${sendToAll ? "전체 유저" : `${pushForm.list.length}명`}`,
    );

    if (!confirmSend) return;

    setLoading(true);

    try {
      const response = await axios.post("/api/notice/sendpush", {
        Time: pushForm.time,
        title: pushForm.title,
        body: pushForm.body,
        users: pushForm.list,
      });

      if (response.status === 200) {
        alert("푸시 발송이 완료되었습니다.");
        // 발송 성공 후 폼 초기화
        setPushForm({
          time: dayjs().format("YYYY-MM-DD HH:mm:ss"),
          title: "",
          body: "",
          list: [],
        });
        setSendToAll(false);
        // TODO: 발송 내역 API 연동 시 fetchHistory() 호출
      }
    } catch (error: any) {
      console.error("푸시 발송 실패:", error);
      alert(
        `푸시 발송에 실패했습니다.\n${error?.response?.data?.message || error.message}`,
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <PageTitle title="앱 푸시 관리" />

      {/* 발송하기 섹션 */}
      <ChatSection style={{ marginTop: 0 }}>
        <ChatSectionTitle>푸시 발송하기</ChatSectionTitle>

        <LineBox>
          <FormContainer>
            <FormRow>
              <FormLabel>발송 시간</FormLabel>
              <FormInputWrapper>
                <Input
                  type="text"
                  placeholder="YYYY-MM-DD HH:mm"
                  value={pushForm.time.slice(0, 16)}
                  onChange={(e) => {
                    const val = e.target.value;
                    setPushForm({
                      ...pushForm,
                      time: val.length === 16 ? val + ":00" : val,
                    });
                  }}
                  style={{ maxWidth: 300 }}
                />
                <FormHint>
                  예약 발송 시간을 설정합니다. (기본: 현재 시간)
                </FormHint>
              </FormInputWrapper>
            </FormRow>

            <FormRow>
              <FormLabel>제목</FormLabel>
              <FormInputWrapper>
                <Input
                  type="text"
                  placeholder="푸시 알림 제목을 입력하세요"
                  value={pushForm.title}
                  onChange={(e) =>
                    setPushForm({ ...pushForm, title: e.target.value })
                  }
                  maxLength={25}
                />
                <FormHint>최대 25자까지 입력 가능합니다.</FormHint>
              </FormInputWrapper>
            </FormRow>

            <FormRow>
              <FormLabel>내용</FormLabel>
              <FormInputWrapper>
                <Textarea
                  placeholder="푸시 알림 내용을 입력하세요"
                  value={pushForm.body}
                  onChange={(e) =>
                    setPushForm({ ...pushForm, body: e.target.value })
                  }
                  maxLength={50}
                />
                <FormHint>최대 50자까지 입력 가능합니다.</FormHint>
              </FormInputWrapper>
            </FormRow>

            <FormRow>
              <FormLabel>발송 대상</FormLabel>
              <FormInputWrapper>
                <Flex style={{ gap: 10, alignItems: "center" }}>
                  <CheckboxLabel
                    style={{ opacity: 0.4, cursor: "not-allowed" }}
                  >
                    <input type="checkbox" checked={sendToAll} disabled />
                    모두에게
                  </CheckboxLabel>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".xlsx,.xls,.csv"
                    style={{ display: "none" }}
                    onChange={handleExcelUpload}
                  />
                  {!sendToAll ? (
                    <ConfirmBtn
                      btnText="엑셀 파일 업로드"
                      btnColor="mint"
                      btnHeight={36}
                      btnClickEvent={() => fileInputRef.current?.click()}
                    />
                  ) : (
                    <></>
                  )}
                  {!sendToAll && pushForm.list.length > 0 ? (
                    <ConfirmBtn
                      btnText="목록 초기화"
                      btnColor="red"
                      btnHeight={36}
                      btnClickEvent={() =>
                        setPushForm({ ...pushForm, list: [] })
                      }
                    />
                  ) : (
                    <></>
                  )}
                </Flex>
                <FormHint>
                  {sendToAll
                    ? "전체 유저에게 발송합니다. (대상: 광고 수신을 동의한 모두)"
                    : <>엑셀 파일의 첫 번째 열에 uid 목록을 작성해주세요. (대상: {pushForm.list.length}명)<br />대상자는 업로드한 유저수 이며, 실 발송은 모계정 기준으로 중복제거된 유저에게 발송됩니다.</>}
                </FormHint>
              </FormInputWrapper>
            </FormRow>

            <FormRow style={{ justifyContent: "flex-end" }}>
              <ConfirmBtn
                btnText="푸시 발송"
                btnColor="mint"
                btnHeight={42}
                btnClickEvent={handleSendPush}
                isDisabled={loading || pushForm.list.length === 0}
              />
            </FormRow>
          </FormContainer>
        </LineBox>
      </ChatSection>

      {/* 발송 내역 섹션 */}
      {/* <ChatSection>
        <ChatSectionTitle>발송 내역</ChatSectionTitle>

        <LineBox>
          <EmptyMessage>아직 발송 내역 API가 연동되지 않았습니다.</EmptyMessage> */}

          {/* API 연동 후 사용할 테이블 */}
          {/* {pushHistory.length > 0 && (
            <Table tableHeadData={tableHead} tableMinWidth={800}>
              <>
                {pushHistory.map((item) => (
                  <TableBody key={item.id}>
                    <TableBodyItem>
                      {dayjs(item.time).format("YYYY-MM-DD HH:mm:ss")}
                    </TableBodyItem>
                    <TableBodyItem>{item.title}</TableBodyItem>
                    <TableBodyItem>{item.body}</TableBodyItem>
                    <TableBodyItem>
                      <StatusBadge status={item.status}>
                        {item.status}
                      </StatusBadge>
                    </TableBodyItem>
                  </TableBody>
                ))}
              </>
            </Table>
          )} */}
        {/* </LineBox>
      </ChatSection> */}

      {loading && <Loader />}
    </>
  );
}

const FormContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding: 20px 0;
`;

const FormRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 20px;
`;

const FormLabel = styled.label`
  min-width: 100px;
  font-size: 15px;
  font-weight: 600;
  color: #2f2f38;
  padding-top: 8px;
`;

const FormInputWrapper = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const FormHint = styled.span`
  font-size: 12px;
  color: #858c97;
`;

const CheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;
  font-weight: 500;
  color: #2f2f38;
  cursor: pointer;
  white-space: nowrap;
`;

const EmptyMessage = styled.div`
  padding: 60px 0;
  text-align: center;
  font-size: 15px;
  color: #858c97;
`;

interface StatusBadgeProps {
  status: string;
}

const StatusBadge = styled.span<StatusBadgeProps>`
  display: inline-block;
  padding: 4px 12px;
  border-radius: 4px;
  font-size: 13px;
  font-weight: 500;
  background-color: ${({ status }) =>
    status === "성공" ? "#e8f5e9" : status === "실패" ? "#ffebee" : "#f5f5f5"};
  color: ${({ status }) =>
    status === "성공" ? "#2e7d32" : status === "실패" ? "#c62828" : "#666"};
`;
